import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, Mail, CalendarClock, MessageSquare, ShieldCheck,
  Phone, Clock,
} from "lucide-react";

const TABS = [
  { id: "clients", label: "Clients", icon: Users },
  { id: "contacts", label: "Contact Form", icon: Mail },
  { id: "appointments", label: "Bookings", icon: CalendarClock },
  { id: "messages", label: "Portal Messages", icon: MessageSquare },
];

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium", timeStyle: "short",
    });
  } catch { return iso; }
}

export default function AdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("clients");
  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== "admin") return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [s, c, m, a, t] = await Promise.all([
          api.get("/admin/summary"),
          api.get("/admin/clients"),
          api.get("/admin/contacts"),
          api.get("/admin/appointments"),
          api.get("/admin/messages"),
        ]);
        if (cancelled) return;
        setSummary(s.data);
        setClients(c.data.clients || []);
        setContacts(m.data.contacts || []);
        setAppointments(a.data.appointments || []);
        setThreads(t.data.threads || []);
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load admin data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (user && user.role !== "admin") {
    return <Navigate to="/portal/dashboard" replace />;
  }

  const stats = [
    { label: "Clients", value: summary?.clients ?? "—", icon: Users },
    { label: "Contact Messages", value: summary?.contact_messages ?? "—", sub: `${summary?.contact_unread ?? 0} new`, icon: Mail },
    { label: "Bookings", value: summary?.appointments ?? "—", sub: `${summary?.appointments_pending ?? 0} pending`, icon: CalendarClock },
    { label: "Portal Messages", value: summary?.portal_messages ?? "—", icon: MessageSquare },
  ];

  const markContact = async (id, status) => {
    try {
      await api.post(`/admin/contacts/${id}/status`, { status });
      setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, status } : c)));
      toast.success(`Marked ${status}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const markAppt = async (id, status) => {
    try {
      await api.post(`/admin/appointments/${id}/status`, { status });
      setAppointments((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
      toast.success(`Marked ${status}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <div data-testid="page-admin" className="space-y-8">
      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#8E6A2B] mb-3">
          <ShieldCheck size={14} /> Admin Dashboard
        </div>
        <h1 className="font-serif text-5xl text-[#0F292F] font-light tracking-tight">
          Practice Inbox
        </h1>
        <p className="mt-3 text-[#52525B]">
          Every lead, signup, booking, and message — in one place.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s+/g,'-')}`} className="bg-white border border-black/10 p-5">
            <s.icon size={18} strokeWidth={1.4} className="text-[#8E6A2B] mb-3" />
            <div className="font-serif text-3xl text-[#0F292F]">{s.value}</div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#52525B] mt-1">{s.label}</div>
            {s.sub && <div className="text-xs text-[#8E6A2B] mt-1">{s.sub}</div>}
          </div>
        ))}
      </div>

      <div className="border-b border-black/10 flex flex-wrap gap-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm transition-colors border-b-2 -mb-px ${
              tab === t.id
                ? "border-[#0F292F] text-[#0F292F] font-medium"
                : "border-transparent text-[#52525B] hover:text-[#0F292F]"
            }`}
          >
            <t.icon size={15} strokeWidth={1.6} />
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 text-center text-[#52525B] text-sm">Loading…</div>
      ) : (
        <>
          {tab === "clients" && <ClientsTab rows={clients} />}
          {tab === "contacts" && <ContactsTab rows={contacts} onStatus={markContact} />}
          {tab === "appointments" && <AppointmentsTab rows={appointments} onStatus={markAppt} />}
          {tab === "messages" && <MessagesTab threads={threads} />}
        </>
      )}
    </div>
  );
}

function ClientsTab({ rows }) {
  if (!rows.length) return <EmptyState label="No client registrations yet." />;
  return (
    <div className="bg-white border border-black/10">
      <table className="w-full text-sm">
        <thead className="border-b border-black/10 text-[10px] uppercase tracking-[0.2em] text-[#52525B]">
          <tr><th className="text-left p-4">Name</th><th className="text-left p-4">Email</th><th className="text-left p-4">Joined</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} data-testid={`admin-client-${r.id}`} className="border-b border-black/5 last:border-b-0 hover:bg-[#F9F8F6]">
              <td className="p-4 font-medium text-[#0F292F]">{r.name || "—"}</td>
              <td className="p-4"><a href={`mailto:${r.email}`} className="text-[#0F292F] hover:text-[#8E6A2B]">{r.email}</a></td>
              <td className="p-4 text-[#52525B]">{fmtDate(r.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ContactsTab({ rows, onStatus }) {
  if (!rows.length) return <EmptyState label="No contact form submissions yet." />;
  return (
    <div className="space-y-4">
      {rows.map((r) => (
        <div key={r.id} data-testid={`admin-contact-${r.id}`} className="bg-white border border-black/10 p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <div className="font-serif text-xl text-[#0F292F]">{r.name}</div>
                <StatusBadge status={r.status || "new"} />
              </div>
              <div className="text-sm text-[#52525B] flex flex-wrap gap-x-4 gap-y-1">
                <a href={`mailto:${r.email}`} className="hover:text-[#0F292F] inline-flex items-center gap-1"><Mail size={12} />{r.email}</a>
                {r.phone && <span className="inline-flex items-center gap-1"><Phone size={12} />{r.phone}</span>}
                <span className="inline-flex items-center gap-1"><Clock size={12} />{fmtDate(r.created_at)}</span>
              </div>
              {r.subject && <div className="text-xs uppercase tracking-[0.2em] text-[#8E6A2B] mt-2">{r.subject}</div>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => onStatus(r.id, "read")} className="text-xs uppercase tracking-[0.15em] px-3 py-2 border border-black/10 hover:bg-[#F9F8F6] text-[#52525B]">Mark Read</button>
              <button onClick={() => onStatus(r.id, "handled")} className="text-xs uppercase tracking-[0.15em] px-3 py-2 bg-[#0F292F] text-white hover:bg-[#173B43]">Mark Handled</button>
            </div>
          </div>
          <p className="mt-4 pt-4 border-t border-black/5 text-[#1A1A1A] whitespace-pre-wrap leading-relaxed">{r.message}</p>
        </div>
      ))}
    </div>
  );
}

function AppointmentsTab({ rows, onStatus }) {
  if (!rows.length) return <EmptyState label="No booking requests yet." />;
  return (
    <div className="space-y-4">
      {rows.map((r) => (
        <div key={r.id} data-testid={`admin-appt-${r.id}`} className="bg-white border border-black/10 p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <div className="font-serif text-xl text-[#0F292F]">{r.name}</div>
                <StatusBadge status={r.status || "requested"} />
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#8E6A2B] mt-1">{r.service}</div>
              <div className="text-sm text-[#0F292F] mt-2">
                <span className="font-medium">{r.preferred_date}</span> · {r.preferred_time}
              </div>
              <div className="text-sm text-[#52525B] mt-2 flex flex-wrap gap-x-4 gap-y-1">
                <a href={`mailto:${r.email}`} className="hover:text-[#0F292F] inline-flex items-center gap-1"><Mail size={12} />{r.email}</a>
                {r.phone && <span className="inline-flex items-center gap-1"><Phone size={12} />{r.phone}</span>}
                <span className="inline-flex items-center gap-1"><Clock size={12} />Requested {fmtDate(r.created_at)}</span>
              </div>
              {r.notes && <p className="mt-3 text-sm text-[#1A1A1A] italic">"{r.notes}"</p>}
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => onStatus(r.id, "confirmed")} className="text-xs uppercase tracking-[0.15em] px-3 py-2 bg-[#0F292F] text-white hover:bg-[#173B43]">Confirm</button>
              <button onClick={() => onStatus(r.id, "completed")} className="text-xs uppercase tracking-[0.15em] px-3 py-2 border border-black/10 hover:bg-[#F9F8F6] text-[#52525B]">Completed</button>
              <button onClick={() => onStatus(r.id, "cancelled")} className="text-xs uppercase tracking-[0.15em] px-3 py-2 border border-black/10 hover:bg-[#F9F8F6] text-[#52525B]">Cancel</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function MessagesTab({ threads }) {
  if (!threads.length) return <EmptyState label="No portal messages yet." />;
  return (
    <div className="space-y-6">
      {threads.map((th) => (
        <div key={th.user_id} data-testid={`admin-thread-${th.user_id}`} className="bg-white border border-black/10">
          <div className="flex items-center justify-between p-5 border-b border-black/10">
            <div>
              <div className="font-serif text-xl text-[#0F292F]">{th.client_name}</div>
              <a href={`mailto:${th.client_email}`} className="text-xs text-[#52525B] hover:text-[#0F292F]">{th.client_email}</a>
            </div>
            <div className="text-xs text-[#52525B] inline-flex items-center gap-1">
              <Clock size={12} /> Last activity {fmtDate(th.last_at)}
            </div>
          </div>
          <div className="p-5 space-y-3 bg-[#F9F8F6]/40">
            {th.messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender === "client" ? "justify-start" : "justify-end"}`}>
                <div className={`max-w-[75%] p-3 text-sm ${m.sender === "client" ? "bg-white border border-black/10 text-[#1A1A1A]" : "bg-[#0F292F] text-white"}`}>
                  <div className={`text-[10px] uppercase tracking-[0.2em] mb-1 ${m.sender === "client" ? "text-[#8E6A2B]" : "text-[#CFA968]"}`}>
                    {m.sender_name} · {fmtDate(m.created_at)}
                  </div>
                  <div className="whitespace-pre-wrap leading-relaxed">{m.body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    new: "bg-[#8E6A2B]/15 text-[#8E6A2B]",
    read: "bg-black/5 text-[#52525B]",
    handled: "bg-[#0F292F]/10 text-[#0F292F]",
    requested: "bg-[#8E6A2B]/15 text-[#8E6A2B]",
    confirmed: "bg-emerald-100 text-emerald-800",
    completed: "bg-black/5 text-[#52525B]",
    cancelled: "bg-red-50 text-red-700",
  };
  return (
    <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 ${map[status] || "bg-black/5 text-[#52525B]"}`}>
      {status}
    </span>
  );
}

function EmptyState({ label }) {
  return (
    <div className="bg-white border border-black/10 p-16 text-center">
      <div className="text-[#52525B]">{label}</div>
    </div>
  );
}
